import { Formatting } from "../formatting";
import { IDataProvider, IRecord } from "./data-providers";
import { IColumn, ICommand, ICurrency, IDataset, IDatasetEventListeners } from "./interfaces";

export class Dataset<T extends IDataProvider> implements IDataset {
    protected _dataProvider: T;
    private _selectedRecordIds: string[] = [];
    private _commands: ICommand[] = [];
    //@ts-ignore - needs to always be defined with injectContext
    private _pcfContext: ComponentFramework.Context<any>;
    private _records: {
        [id: string]: IRecord;
    } = {};
    private _sortedRecordIds: string[] = [];
    //@ts-ignore registered through registerEventHandler
    private _eventListeners: {
        [K in keyof IDatasetEventListeners]: IDatasetEventListeners[K][];
    } = {};
    private _renderer: () => void = () => {
        throw new Error('No rendered specified! Use setRenderer to specify your control renderer.');
    };
    constructor(dataProvider: T) {
        this._dataProvider = dataProvider;
    }
    public get columns(): IColumn[] {
        return [...this._dataProvider.getColumns()];
    }
    public setColumns(columns: Parameters<T['setColumns']>[0]) {
        this._dataProvider.setColumns(columns);
    }
    public setDataSource(dataSource: Parameters<T['setDataSource']>[0]) {
        this._dataProvider.setDataSource(dataSource);
    }
    public getDataSource(): ReturnType<T['getDataSource']> {
        return this._dataProvider.getDataSource();
    }
    public _setRenderer(renderer: () => Promise<void> | void) {
        this._renderer = renderer;
    }
    public get error(): boolean {
        return this._dataProvider.isError();
    }
    public get errorMessage(): string {
        return this._dataProvider.getErrorMessage();
    }
    public get loading(): boolean {
        return this._dataProvider.isLoading();
    }
    public get sorting(): ComponentFramework.PropertyHelper.DataSetApi.SortStatus[] {
        return this._dataProvider.getSorting();
    }
    public set sorting(sort: ComponentFramework.PropertyHelper.DataSetApi.SortStatus[]) {
        this._dataProvider.setSorting(sort);
    }
    public get linking(): ComponentFramework.PropertyHelper.DataSetApi.Linking {
        return {
            getLinkedEntities: () => structuredClone(this._dataProvider.getLinking()),
            addLinkedEntity: (expr) => {
                const linking = this._dataProvider.getLinking();
                linking.push(expr);
                this._dataProvider.setLinking(linking);
            }
        };
    }
    public get paging(): IDataset['paging'] {
        return {
            ...this._dataProvider.getPaging(),
            loadExactPage: async (pageNumber, save?: boolean) => {
                return new Promise((resolve) => {
                    this.clearSelectedRecordIds();
                    this._renderDecorator(async () => {
                        if (save) {
                            await this._save();
                            this.clearChanges();
                        }
                        const records = await this._dataProvider.getPaging().loadExactPage(pageNumber);
                        resolve(records);
                    });
                });
            },
            loadPreviousPage: async () => {
                this.clearSelectedRecordIds();
                await this._renderDecorator(() => this._dataProvider.getPaging().loadPreviousPage());
            },
            loadNextPage: async () => {
                this.clearSelectedRecordIds();
                await this._renderDecorator(() => this._dataProvider.getPaging().loadNextPage());
            },
            setPageSize: (pageSize: number) => {
                this._dataProvider.getPaging().setPageSize(pageSize);
                this._fireEventListeners('onPageSizeChanged', pageSize);
            },
            reset: async () => {
                this.refresh();
            }
        };
    }
    public get filtering(): IDataset['filtering'] {
        return {
            setFilter: (expr) => {
                this._dataProvider.setFiltering(expr);
            },
            clearFilter: () => {
                this._dataProvider.setFiltering(null);
            },
            getFilter: () => {
                return structuredClone(this._dataProvider.getFiltering());
            }
        };
    }
    public get records(): { [id: string]: IRecord } {
        //return the cached records if we have them
        //this for performance
        for (const rec in this._records) {
            if (rec) {
                return this._records;
            }
        }
        this._dataProvider.getRecords().map(record => {
            const change = this.getChanges()[record.getRecordId()];
            if (change) {
                record = change.record;
            }
            this._records[record.getRecordId()] = record;
            this._fireEventListeners('onRecordLoaded', record);
        });
        return this._records;
    }

    public get sortedRecordIds(): string[] {
        if (this._sortedRecordIds.length > 0) {
            return this._sortedRecordIds;
        }
        this._sortedRecordIds = Object.entries(this.records).map(x => x[0]);
        return this._sortedRecordIds;
    }
    public clearSelectedRecordIds(): void {
        this.setSelectedRecordIds([]);
    }
    public getSelectedRecordIds(): string[] {
        return [...this._selectedRecordIds];
    }
    public setSelectedRecordIds(ids: string[]): void {
        this._selectedRecordIds = ids;
        this._fireEventListeners('onRecordsSelected', ids);
    }
    public getTargetEntityType(): string {
        return this._dataProvider.getEntityName();
    }
    public getTitle(): string {
        return this._dataProvider.getTitle();
    }
    public setTitle(title: string) {
        this._dataProvider.setTitle(title);
    }
    public setCurrencies(currencies: ICurrency[]) {
        this._dataProvider.setCurrencies(currencies);
    }
    public getViewId(): string {
        return this._dataProvider.getViewId();
    }
    public openDatasetItem(entityReference: ComponentFramework.EntityReference): void {
        const openDatasetItemEvents = this._eventListeners.onDatasetItemOpened ?? [];
        if (openDatasetItemEvents.length > 0) {
            //only call the last defined event
            openDatasetItemEvents.pop()?.(entityReference);
        }
        else {
            this._dataProvider.openDatasetItem(entityReference);
        }
    }
    public async refresh(save?: boolean): Promise<void> {
        return new Promise((resolve) => {
            this.clearSelectedRecordIds();
            if (save) {
                this._save();
            }
            this._renderDecorator(async () => {
                if (save) {
                    this.clearChanges();
                }
                await this._dataProvider.refresh();
                resolve();
            });
        });
    }
    public getMetadata(): ReturnType<T['getMetadata']> {
        return this._dataProvider.getMetadata();
    }
    public setMetadata(metadata: Parameters<T['setMetadata']>[0]): void {
        this._dataProvider.setMetadata(metadata);
    }
    public retrieveRecordCommand(recordIds: string[] = [], specificCommands: string[], filterByPriority: boolean, useNestedFormat: boolean, refreshAllRules: boolean): any {
        return this._commands;
    }
    public setSearchQuery(query?: string) {
        this._dataProvider.setSearchQuery(query);
    }
    public getSearchQuery(): string | null {
        return this._dataProvider.getSearchQuery();
    }

    public hasInvalidChanges() {
        return this._dataProvider.hasInvalidChanges();
    }

    public isDirty() {
        return this._dataProvider.isDirty();
    }

    public getChanges() {
        return this._dataProvider.getChanges();
    }
    public clearChanges() {
        this._dataProvider.clearChanges();
        this._fireEventListeners('onChangesCleared');
    }
    public async save(): Promise<void> {
        return new Promise((resolve) => {
            this._renderDecorator(async () => {
                await this._save();
                this.clearChanges();
                resolve();
            });
        });
    }

    public _patchContext(pcfContext: ComponentFramework.Context<any>): ComponentFramework.Context<any> {
        if (!this._pcfContext) {
            const factory = this._getShallowCopyWithPrototype(pcfContext.factory);
            const utils = this._getShallowCopyWithPrototype(pcfContext.utils);
            factory.requestRender = () => this.render();
            utils.getEntityMetadata = (entityName, attributes) => this._getEntityMetadata(entityName, attributes, pcfContext);
            this._pcfContext = {
                ...pcfContext,
                formatting: Formatting.Get(),
                factory: factory,
                utils: utils
            };
        }
        else {
            this._pcfContext.utils.getEntityMetadata = (entityName, attributes) => this._getEntityMetadata(entityName, attributes, pcfContext);
        }
        return this._pcfContext;
    }

    public render(): Promise<void> | void {
        return this._render();
    }

    public addEventListener<K extends keyof IDatasetEventListeners>(event: K, eventListener: IDatasetEventListeners[K]) {
        if (!this._eventListeners[event]) {
            this._eventListeners[event] = [];
        }
        this._eventListeners[event].push(eventListener);
        this._dataProvider.addEventListener(event, eventListener);
    }

    public _fireEventListeners<K extends keyof IDatasetEventListeners>(event: K, ...par: Parameters<IDatasetEventListeners[K]>): Array<ReturnType<IDatasetEventListeners[K]>> {
        //@ts-ignore - typings
        return this._eventListeners[event]?.map(callback => callback(...par)) ?? [];
    }

    private _getShallowCopyWithPrototype<T>(originalObject: T): T {
        return Object.setPrototypeOf({ ...originalObject }, Object.getPrototypeOf(originalObject));
    }

    private async _save() {
        const changes = Object.values(this.getChanges());
        //do not attempt to save if no changes are present
        if (changes.length === 0) {
            return;
        }
        const savePromises = changes.map(change => {
            return change.record.save();
        });
        return Promise.all(savePromises);
    }

    private _render(): Promise<void> | void {
        //render method should make sure the context is always patched in
        /*         if (!this._pcfContext) {
                    throw new Error('You need to inject PCF context via _pathContext first!. New context should be injected on every updateView call.');
                } */
        return this._renderer();
    }
    private async _renderDecorator(fn: () => Promise<any> | any): Promise<void> {
        this._dataProvider.setLoading(true);
        await this.render();
        await fn();
        this._records = {};
        this._sortedRecordIds = [];
        this._records = {};
        this._sortedRecordIds = [];
        this._fireEventListeners('onNewDataLoaded');
        this._dataProvider.setLoading(false);
        await this.render();
    }

    private async _getEntityMetadata(entityName: string, attributeNames: string[] | undefined, pcfContext: ComponentFramework.Context<any>) {
        const originalGetEntityMetadata = pcfContext.utils.getEntityMetadata.bind(pcfContext.utils);
        const enititiesToOverride = [this._dataProvider.getEntityName(), ...this.linking.getLinkedEntities().map(x => x.name)];
        if (!enititiesToOverride.includes(entityName)) {
            return originalGetEntityMetadata(entityName, attributeNames);
        }
        const attributes: any = {
            //TODO: implement all delegates
            get: (name: string) => {
                let metadata = this._dataProvider.getColumns().find(x => x.name === name)?.metadata;
                const linkedEntity = this.linking.getLinkedEntities().find(x => x.name === entityName);
                if (linkedEntity) {
                    metadata = this._dataProvider.getColumns().find(x => x.name === `${linkedEntity.alias}.${name}`)?.metadata;
                }
                metadata = { ...this._invokeGetters(metadata) };
                return {
                    ...metadata ?? {},
                    attributeDescriptor: metadata ?? {}
                };
            },
        };
        const entityMetadata = await (this.linking.getLinkedEntities().find(x => x.name === entityName) ? originalGetEntityMetadata(entityName, attributeNames) : this._dataProvider.getMetadata());
        return {
            ...this._invokeGetters(entityMetadata),
            Attributes: attributes
        };
    }
    //we are passing the metadata as class instance in Portal, this results in the getter values not being transfered when spread operator is used
    private _invokeGetters(obj: any) {
        const result: any = {};
        const keys = Object.getOwnPropertyNames(Object.getPrototypeOf(obj ?? {}));
        if (keys.includes('__proto__')) {
            //passed object is not an instance of class => getters will be called automatically with spread operator
            return obj;
        }
        for (const key of keys) {
            if (key === 'constructor') {
                continue;
            }
            try {
                result[key] = obj[key];
            }
            catch (err) {
            }
        }
        return result;
    }
}